import { useState, useEffect } from "react";
import { createFileRoute, redirect, Link, useNavigate } from "@tanstack/react-router";
import CheckEmail from "@/components/singin/CheckEmail";
import ResendOrSignInLinks from "@/components/singin/ResendOrSignInLinks";
import SetPassword from "@/components/singin/SetPassword";
import { useAuth } from "@/providers/authProvider";
import { z } from "zod";
import { ServiceLogo1Line } from "@/components/ui/iconsvg";
import {
  AuthRelayError,
  signInWithEmailLinkViaAPI,
  updatePasswordViaAPI,
  isSignInWithEmailLink,
} from "@/providers/auth";
import { getEmailLinkError } from "@/lib/emailLinkError";
import { queryClient } from "@/main";


const fallback = "/competitions" as const;

// このブラウザでメールリンクのサインインに成功し、パスワード設定がまだのリンクの oobCode。
// リンクは使い捨てなので、設定前に再読み込みしたり同じリンクを開き直したりしたときは、
// 「別のアカウントでサインイン中」ではなくパスワード設定に戻すために使う
const PENDING_PASSWORD_KEY = "emailLinkPendingPassword";
const currentOobCode = () => new URL(window.location.href).searchParams.get("oobCode");

export const Route = createFileRoute("/auth-callback")({
  validateSearch: z.object({
    redirect: z.string().optional().catch(""),
  }),
  beforeLoad: ({ context, search, location }) => {
    // 登録リンク（mode=signIn）はサインイン中でも画面側で扱う。
    // 別アカウントでサインイン中のブラウザで開いたときに、リンクを処理しないまま遷移させないため。
    const href = new URL(location.href, window.location.origin).href;
    if (context.auth.isAuthenticated && !isSignInWithEmailLink(href)) {
      throw redirect({ to: search.redirect || fallback });
    }
  },
  component: AuthCallback,
});

type AuthState =
  | { status: "checking" }
  | { status: "signedInAsOther" }
  | { status: "checkEmail" }
  | { status: "setPassword" }
  | { status: "success" }
  | { status: "linkError"; message: string };

function AuthCallback() {
  const [authState, setAuthState] = useState<AuthState>({ status: "checking" });

  const navigate = useNavigate();
  const search = Route.useSearch();
  const { user, isAuthenticated, verifyToken, logout } = useAuth();

  // 成功時はパスワード設定へ、リンク自体が使えないときはエラー画面へ進める。
  // メールアドレスを入力し直せば済むエラーのときだけ、その文言を返す
  const signIn = async (email: string): Promise<string | undefined> => {
    try {
      await signInWithEmailLinkViaAPI(email, window.location.href);
    } catch (error) {
      console.error("Error signing in with email link:", error);
      const { message, target } = getEmailLinkError(
        error instanceof AuthRelayError ? error.code : undefined
      );
      if (target === "link") {
        setAuthState({ status: "linkError", message });
        return;
      }
      return message;
    }
    localStorage.setItem(PENDING_PASSWORD_KEY, currentOobCode() ?? "");
    await verifyToken();
    setAuthState({ status: "setPassword" });
  };

  const setInitialPassword = async (
    password: string
  ): Promise<string | undefined> => {
    if (!user){
      return "ユーザが認証されていません。"
    }
    try {
      await updatePasswordViaAPI(user.uid, password);
    } catch (error) {
      console.error("Error setting initial password:", error);
      return "パスワードの設定に失敗しました。時間をおいて再度お試しください。";
    }
    localStorage.removeItem(PENDING_PASSWORD_KEY);
    setAuthState({ status: "success" });
    navigate({ to: search.redirect || fallback });
  };

  const processEmailLink = async () => {
    if (!isSignInWithEmailLink(window.location.href)) {
      setAuthState({ status: "linkError", message: getEmailLinkError("invalid-link").message });
      return;
    }
    // /signin-email で再送した端末では、保存したメールアドレスで自動的にサインインする
    const storedEmail = localStorage.getItem("emailForSignIn");
    if (!storedEmail) {
      setAuthState({ status: "checkEmail" });
      return;
    }
    // 失敗しても次回以降に同じメールアドレスで自動サインインを繰り返さないよう、先に削除する
    localStorage.removeItem("emailForSignIn");
    // 保存していたメールアドレスが一致しないなどの場合は、入力画面で入力し直してもらう
    if (await signIn(storedEmail)) {
      setAuthState({ status: "checkEmail" });
    }
  };

  const signOutAndContinue = async () => {
    setAuthState({ status: "checking" });
    await logout();
    queryClient.clear();
    await processEmailLink();
  };

  useEffect(() => {
    if (isAuthenticated) {
      const pending = localStorage.getItem(PENDING_PASSWORD_KEY);
      setAuthState({
        status: pending && pending === currentOobCode() ? "setPassword" : "signedInAsOther",
      });
      return;
    }
    processEmailLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="w-screen min-h-screen bg-slate-50">
    <header className="w-full h-[80px]">
      <div className="flex items-center justify-between h-full max-w-screen-2xl mx-auto p-4">
        <div className="logo">
          <ServiceLogo1Line /><p className="hidden">スキルアップAI コンペティション</p>
        </div>
      </div>
      <div className="w-full border border-b-slate-100"></div>
    </header>
    <main>
    <div>
      {authState.status === "checking" && (
        <p className="mt-32 text-center">認証情報を確認しています…</p>
      )}
      {authState.status === "success" && (
        <p className="mt-32 text-center">アカウント設定が完了しました。画面を移動しています…</p>
      )}
      {authState.status === "signedInAsOther" && (
        <section className="mt-32 mx-auto w-fit">
          <div className="bg-white p-8 rounded-xl shadow-lg max-w-xl">
            <h1 className="text-3xl">
              <span>別のアカウントでサインイン中です</span>
            </h1>
            <p className="text-lg mt-4">
              {user?.email ? `${user.email} でサインインしています。` : ""}
              このリンクでアカウント設定を行うには、サインアウトしてください。
            </p>
            <div className="flex flex-col items-center gap-4 mt-8">
              <button
                type="button"
                className="text-lg px-24 py-3"
                onClick={signOutAndContinue}
              >
                サインアウトして続ける
              </button>
              <p className="text-sm text-slate-600 underline">
                <Link to={search.redirect || fallback}>
                  サインイン中のアカウントで利用する
                </Link>
              </p>
            </div>
          </div>
        </section>
      )}
      {authState.status === "linkError" && (
        <section className="mt-32 mx-auto w-fit">
          <div className="bg-white p-8 rounded-xl shadow-lg max-w-xl">
            <h1 className="text-3xl">
              <span>リンクをご利用いただけません</span>
            </h1>
            <p className="text-lg mt-4 text-red-600">{authState.message}</p>
            <ResendOrSignInLinks />
          </div>
        </section>
      )}
      {authState.status === "checkEmail" && (
        <section className="mt-32 mx-auto w-fit">
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h1 className="text-3xl">
              <span>メールアドレス設定</span>
            </h1>
            <p className="text-lg mb-2">
              アカウントに登録するメールアドレスを入力してください。
            </p>
            <CheckEmail
              updateEmail={signIn}
              message="登録案内を受信したメールアドレスを入力してください。"
            />
          </div>
        </section>
      )}
      {authState.status === "setPassword" && (
        <SetPassword updatePassword={setInitialPassword} />
      )}
    </div>
    </main>
  </div>

  );
}
